import { useCallback, useEffect, useRef, useState } from 'react';

import MapEngine, { isBaiduMapGLReady, loadBaiduMapGL } from '@/map-core';
import { MapEditorCoordinator } from '../controller/MapEditorCoordinator';
import type { IStoreBridge } from '../controller/storeBridge';
import { useEditorStore } from '../store/editorStore';
import { useSearchStore } from '../store/searchStore';
import { useDataStore } from '../store/dataStore';
import { useLayerStore } from '../store/layerStore';
import { useRoutePlanStore } from '../store/routePlanStore';
import { useMapLayers } from './useMapLayers';
import { tileLayer } from '../utils/config';

const INITIAL_MAP_CENTER: [number, number] = [
  116.55762943048826, 39.78524594729655,
];

const BAIDU_MAP_AK = process.env.JDX_APP_BAIDU_MAP_AK || '';

/**
 * 组装器（Composition Root）— 桥接 React ↔ Coordinator
 *
 * 层级：hooks（"React 生命周期"）
 *
 * 职责：
 * 1. 地图 SDK 加载 — 通过 loadBaiduMapGL 加载 BMapGL
 * 2. 地图初始化 — SDK 就绪后创建 MapEngine，注册 drawCallBack/selectCallBack
 * 3. Coordinator 创建 — 依赖 map 实例，创建 MapEditorCoordinator 单例
 * 4. 依赖注入 — overlayRoot、SearchManager UI 回调、moveend 监听
 * 5. 图层响应式 — 通过 useMapLayers 统一维护 WMS / VectorTile
 * 6. 暴露稳定 API — coordinatorRef / mapEngineRef / 搜索 UI 状态 / 就绪状态
 *
 * 地图引擎和 Coordinator 在整个组件生命周期内只创建一次，
 * 其所有方法引用永远稳定，不会因 React 重渲染而重建
 */
export function useMapBootstrap(
  mapSdkMountRef: React.MutableRefObject<HTMLDivElement | null>,
  overlayRootRef: React.MutableRefObject<HTMLDivElement | null>
) {
  const mapEngineRef = useRef<MapEngine | null>(null);
  const coordinatorRef = useRef<MapEditorCoordinator | null>(null);
  const [isBaiduMapReady, setIsBaiduMapReady] = useState(isBaiduMapGLReady());
  const [baiduMapError, setBaiduMapError] = useState<Error | null>(null);
  // 用 state 追踪 Controller 是否就绪，创建后触发重渲染
  const [isReady, setIsReady] = useState(false);
  // 地图瓦片首次加载完成标记：BMapGL 初始化期间工具栏下拉在 micro-app 宿主中会挂载失败（见 AGENTS.md 坑17），
  // 因此在 tilesloaded 之前禁用工具栏交互
  const [isMapTilesLoaded, setIsMapTilesLoaded] = useState(false);

  /** 标记地图瓦片加载完成；幂等，tilesloaded 重复触发或兜底超时重复调用均无副作用 */
  const markMapTilesLoaded = useCallback(() => {
    setIsMapTilesLoaded(true);
  }, []);

  // 搜索 UI 局部状态（不适合放 store，保留在 React 中）
  const [locationOptionList, setLocationOptionList] = useState<any[]>([]);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  // 图层显隐状态
  const hiddenWmsLayerIds = useLayerStore((s) => s.hiddenWmsLayerIds);
  const ldMapVisible = useLayerStore((s) => s.ldMapVisible);
  const positionSearchVal = useSearchStore((s) => s.positionSearchVal);

  // 加载百度地图 SDK
  useEffect(() => {
    console.log('[useMapBootstrap] SDK load effect start', { isBaiduMapGLReady: isBaiduMapGLReady() });
    if (isBaiduMapGLReady()) {
      console.log('[useMapBootstrap] SDK already ready');
      setIsBaiduMapReady(true);
      return;
    }
    let disposed = false;
    loadBaiduMapGL({ ak: BAIDU_MAP_AK })
      .then(() => {
        console.log('[useMapBootstrap] loadBaiduMapGL resolved', { disposed });
        if (!disposed) {
          setIsBaiduMapReady(true);
          setBaiduMapError(null);
        }
      })
      .catch((err) => {
        console.error('[useMapBootstrap] loadBaiduMapGL rejected', err);
        if (!disposed) {
          setBaiduMapError(err instanceof Error ? err : new Error(String(err)));
          setIsBaiduMapReady(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, []);

  // 地图初始化 + Controller 创建
  useEffect(() => {
    console.log('[useMapBootstrap] init effect', {
      isBaiduMapReady,
      hasContainer: !!mapSdkMountRef.current,
    });
    if (!isBaiduMapReady || !mapSdkMountRef.current) return;

    let tilesLoadedFallbackTimer: number | null = null;
    try {
      /**
       * 创建地图引擎，并把业务协调器的处理函数以回调形式注入。
       *
       * 为什么在这里传回调？
       * - MapEngine（map-core 层）只关心"地图怎么动"，不能依赖 MapEditorCoordinator（controller 层）
       * - MapEditorCoordinator 只关心"业务怎么做"，不能依赖 BMapGL 细节
       * - useMapBootstrap 是组合根，唯一同时知道两层的存在，负责在创建时把地图事件转发给业务层
       *
       * 回调映射：
       * - drawCallBack   → 绘制完成（双击/吸附闭合）→ Coordinator.handleDrawComplete
       * - selectCallBack → 多边形选中/取消选中     → Coordinator.handleSelect
       * - onDrawError    → 绘制校验失败              → Coordinator.handleDrawError
       * - onDrawCancel   → ESC 取消绘制              → Coordinator.handleDrawCancel
       */
      console.log('[useMapBootstrap] creating MapEngine...');
      mapEngineRef.current = new MapEngine({
        container: mapSdkMountRef.current,
        centerPoint: INITIAL_MAP_CENTER,
        drawCallBack: (result, feature, id, coordinates) =>
          coordinatorRef.current?.handleDrawComplete(result, feature, id, coordinates),
        selectCallBack: (feature, id, type) =>
          coordinatorRef.current?.handleSelect(feature, id, type),
        onDrawError: (reason: string) =>
          coordinatorRef.current?.handleDrawError(reason),
        onDrawCancel: () => coordinatorRef.current?.handleDrawCancel(),
      });
      console.log('[useMapBootstrap] MapEngine created');

      // 创建业务协调器（依赖地图引擎实例 + store 桥接）
      console.log('[useMapBootstrap] creating Coordinator...');

      // 构建 store bridge：将 Zustand store 的读写方法注入 Controller 层
      // 这样 Controller 不再直接依赖 Zustand，便于测试和解耦
      const storeBridge: IStoreBridge = {
        editor: {
          getSession: () => useEditorStore.getState().session,
          setBrowseMode: () => useEditorStore.getState().setBrowseMode(),
          setViewMode: (data, feature) => useEditorStore.getState().setViewMode(data, feature),
          setEditMode: (data) => useEditorStore.getState().setEditMode(data),
          setCreateDrawingMode: () => useEditorStore.getState().setCreateDrawingMode(),
          setCreateFormMode: (draft, lightMapDraft) =>
            useEditorStore.getState().setCreateFormMode(draft, lightMapDraft),
          updateActivePolygonData: (data) => useEditorStore.getState().updateActivePolygonData(data),
          getPopupContent: () => useEditorStore.getState().popupContent,
          setPopupContent: (content) => useEditorStore.getState().setPopupContent(content),
          getVariableList: () => useDataStore.getState().variableList ?? [],
          subscribeSession: (callback) => useEditorStore.subscribe(callback),
          setLightMapDraft: (draft) => useEditorStore.getState().setLightMapDraft(draft),
          clearLightMapDraft: () => useEditorStore.getState().clearLightMapDraft(),
        },
        search: {
          getPositionSearchVal: () => useSearchStore.getState().positionSearchVal,
          setPositionSearchVal: (updater) => useSearchStore.getState().setPositionSearchVal(updater),
          getRightSearchVal: () => useSearchStore.getState().rightSearchVal,
          setRightSearchVal: (val) => useSearchStore.getState().setRightSearchVal(val),
          getCityList: () => useSearchStore.getState().cityList,
          setCityList: (list) => useSearchStore.getState().setCityList(list),
          getStopOptionList: () => useSearchStore.getState().stopOptionList,
          setStopOptionList: (list) => useSearchStore.getState().setStopOptionList(list),
          getVehicleOptionList: () => useSearchStore.getState().vehicleOptionList,
          setVehicleOptionList: (list) => useSearchStore.getState().setVehicleOptionList(list),
        },
        data: {
          setVariableList: (list) => useDataStore.getState().setVariableList(list),
          setPageData: (updater) => useDataStore.getState().setPageData(updater),
        },
        layer: {
          getHiddenDynamicLayerIds: () => useLayerStore.getState().hiddenDynamicLayerIds,
          getHiddenWmsLayerIds: () => useLayerStore.getState().hiddenWmsLayerIds,
          toggleDynamicLayer: (id) => useLayerStore.getState().toggleDynamicLayer(id),
          toggleWmsLayer: (id) => useLayerStore.getState().toggleWmsLayer(id),
        },
        routePlan: {
          getSnapshot: () => useRoutePlanStore.getState(),
          enterPlanMode: () => useRoutePlanStore.getState().enterPlanMode(),
          exitPlanMode: () => useRoutePlanStore.getState().exitPlanMode(),
          setPointSourceMode: (mode) => useRoutePlanStore.getState().setPointSourceMode(mode),
          setRouteStage: (stage) => useRoutePlanStore.getState().setRouteStage(stage),
          setStartPoint: (point) => useRoutePlanStore.getState().setStartPoint(point),
          setEndPoint: (point) => useRoutePlanStore.getState().setEndPoint(point),
          setStartInput: (text) => useRoutePlanStore.getState().setStartInput(text),
          setEndInput: (text) => useRoutePlanStore.getState().setEndInput(text),
          setInputError: (which, msg) => useRoutePlanStore.getState().setInputError(which, msg),
          setRouteResult: (distance, duration) =>
            useRoutePlanStore.getState().setRouteResult(distance, duration),
          clearRouteResult: () => useRoutePlanStore.getState().clearRouteResult(),
          setPlanError: (msg) => useRoutePlanStore.getState().setPlanError(msg),
          resetPoints: () => useRoutePlanStore.getState().resetPoints(),
        },
      };

      const coordinator = new MapEditorCoordinator(mapEngineRef.current, storeBridge);
      coordinatorRef.current = coordinator;
      console.log('[useMapBootstrap] Coordinator created');

      // 创建时直接注入依赖，不需要后续无依赖 effect
      coordinator.setOverlayRoot(overlayRootRef.current);
      coordinator.search.setUICallbacks({
        updateLocationOptions: setLocationOptionList,
        updateDropdownOpen: setIsLocationDropdownOpen,
        updateDynamicMap: (pos: any, extra: any) => {
          coordinator.suppressNextMoveEnd();
          const store = useSearchStore.getState();
          coordinator.resetInteractionState();
          coordinator.map.changeSearchPoint([pos.lon, pos.lat]);
          coordinator.data.fetchList(
            { ...store.positionSearchVal, lon: pos.lon, lat: pos.lat },
            extra,
            { resetPage: true }
          );
          store.setRightSearchVal(extra);
        },
      });

      const [lon, lat] = INITIAL_MAP_CENTER;
      const initialPosition = { lon, lat, longitude: lon, latitude: lat };

      useSearchStore.getState().setPositionSearchVal((prev: any) => ({
        ...prev,
        ...initialPosition,
      }));
      coordinatorRef.current.fetchRecordList(initialPosition, null, { resetPage: true });

      // BMapGL 初始化期间工具栏下拉在 micro-app 宿主中会挂载失败（AGENTS.md 坑17），
      // 监听首次 tilesloaded 后放开工具栏交互；
      // 4s 兜底：地图容器不可见时 tilesloaded 可能不触发，避免工具栏永久禁用
      mapEngineRef.current.map.addEventListener('tilesloaded', markMapTilesLoaded);
      tilesLoadedFallbackTimer = window.setTimeout(markMapTilesLoaded, 4000);

      console.log('[useMapBootstrap] setIsReady(true)');
      setIsReady(true);
    } catch (err) {
      console.error('[useMapBootstrap] Failed to init map/Controller:', err);
      setBaiduMapError(err instanceof Error ? err : new Error(String(err)));
    }

    return () => {
      if (tilesLoadedFallbackTimer !== null) {
        window.clearTimeout(tilesLoadedFallbackTimer);
      }
      mapEngineRef.current?.map?.removeEventListener('tilesloaded', markMapTilesLoaded);
      setIsMapTilesLoaded(false);
      coordinatorRef.current?.destroy();
      mapEngineRef.current?.destroy?.();
      document.querySelectorAll('.ol-popup').forEach((node) => node.remove());
      mapEngineRef.current = null;
      coordinatorRef.current = null;
      setIsReady(false);
      document
        .querySelectorAll('.ant-popover, .ant-tooltip')
        .forEach((el) => el.remove());
    };
    // 安全忽略：mapSdkMountRef 是 useRef 对象引用不变；setState setter 引用不变；
    // useSearchStore.getState() 在调用时读最新值不依赖闭包
  }, [isBaiduMapReady, markMapTilesLoaded]);

  // 注册 moveend 监听（通过 MapEventManager），委托给 Controller.handleMoveEnd
  useEffect(() => {
    const ctrl = coordinatorRef.current;
    if (!ctrl || !mapEngineRef.current) return;
    const removeListener = mapEngineRef.current.events.on('map:moveend', () => {
      coordinatorRef.current?.handleMoveEnd();
    });
    return () => {
      removeListener?.();
    };
  }, [isReady]);

  // 初始化搜索选项：依赖 isReady，确保 Coordinator 创建完成后再调用
  useEffect(() => {
    if (isReady) {
      coordinatorRef.current?.search.init();
    }
  }, [isReady]);

  // 统一图层响应式管理
  useMapLayers(
    mapEngineRef,
    isBaiduMapReady,
    positionSearchVal.mapId,
    positionSearchVal.mapVersion,
    hiddenWmsLayerIds,
    ldMapVisible,
    { url: 'https://api.map.baidu.com/api_maptile/v1/vlink' }
  );

  return {
    isBaiduMapReady,
    baiduMapError,
    isControllerReady: isReady,
    isMapTilesLoaded,
    coordinatorRef,
    mapEngineRef,
    locationOptionList,
    setLocationOptionList,
    isLocationDropdownOpen,
    setIsLocationDropdownOpen,
  };
}
